import React, { useState, useEffect, type Dispatch, type SetStateAction } from "react";
import { TailSpin } from "react-loader-spinner";
import { showError } from "../../../../../utils/toast.ts";
import ActionBanner from "../../components/action-banner/action-banner.tsx";
import TimesheetEntryCard from "../../components/timesheet-entry-card/timesheet-entry-card.tsx";
import { TimesheetEntry } from "../../types";
import { SubmitConfirmationModal } from "../../components/submit-confirmation-modal/submit-confirmation-modal.tsx";
import { useTimesheetMutation } from "../../hooks/use-timesheet-mutation.ts";
import type { ProjectAllocationRow } from "../../utils/timesheet-allocation.ts";
import { getAllocationSubmitError } from "../../utils/timesheet-allocation.ts";
import { isTimesheetHistoricalViewOnly } from "../../utils/timesheet-allowed-dates.ts";
import { isRejectedEditableEntry } from "../../utils/timesheet-entry-flags.ts";
import styles from "./time-entry-tab.module.css";

interface TimeEntryTabProps {
  entries: TimesheetEntry[];
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
  handleAddEntry: (entry: TimesheetEntry) => void;
  handleDeleteEntry: (id: string) => void;
  handleEditEntry: (updated: TimesheetEntry) => void;
  draftId: number | null;
  setDraftId?: (id: number | null) => void;
  isSubmitted: boolean;
  setIsSubmitted?: (val: boolean) => void;
  statusInfo?: {
    expired?: boolean;
    projectAllocations?: ProjectAllocationRow[];
  };
  projectAllocations?: ProjectAllocationRow[];
  getOtherEntriesMinutesForProject?: (projectId: number) => number;
  showExtendButton?: boolean;
  extensionHrStatus?: string | null;
  onExtendClick?: () => void;
  isExtendLoading?: boolean;
  setEntries?: Dispatch<SetStateAction<TimesheetEntry[]>>;
  onRefetch?: () => void | Promise<void>;
  isRefreshing?: boolean;
  historicalViewOnly?: boolean;
  timesheetLockMessage?: string;
}

const TimeEntryTab = ({
  entries,
  selectedDate,
  setSelectedDate,
  handleAddEntry,
  handleDeleteEntry,
  handleEditEntry,
  draftId,
  setDraftId,
  isSubmitted,
  setIsSubmitted,
  statusInfo,
  projectAllocations,
  getOtherEntriesMinutesForProject,
  showExtendButton,
  extensionHrStatus,
  onExtendClick,
  isExtendLoading,
  setEntries,
  onRefetch,
  isRefreshing = false,
  historicalViewOnly: historicalViewOnlyProp,
  timesheetLockMessage,
}: TimeEntryTabProps) => {
  const [isSubmitConfirmOpen, setIsSubmitConfirmOpen] = useState(false);
  const [isDraftSavedLocally, setIsDraftSavedLocally] = useState(false);
  const { mutateTimesheet, isSavingDraft, isSubmitting } =
    useTimesheetMutation();

  const historicalViewOnly =
    historicalViewOnlyProp ?? isTimesheetHistoricalViewOnly(selectedDate);
  const correctionMode =
    isSubmitted && entries.some((e) => isRejectedEditableEntry(e));

  useEffect(() => {
    setIsDraftSavedLocally(false);
  }, [selectedDate]);

  const handleSaveDraft = async () => {
    const data: any = await mutateTimesheet(selectedDate, entries, 0, draftId, correctionMode);
    if (!data) return;

    setIsDraftSavedLocally(true);

    if (data?.id) {
      setDraftId?.(data.id);
    }
  };

  const handleConfirmSubmit = async () => {
    const allocErr = getAllocationSubmitError(
      entries,
      projectAllocations ?? statusInfo?.projectAllocations,
    );
    if (allocErr) {
      showError(allocErr);
      return;
    }

    try {
      const data: any = await mutateTimesheet(
        selectedDate,
        entries,
        1,
        draftId,
        correctionMode
      );
      if (data === undefined) return;

      if (data && data.id) {
        setDraftId?.(data.id);
      }
      setIsSubmitConfirmOpen(false);
      await onRefetch?.();
    } catch (err) {
      console.error("Submission failed:", err);
    }
  };

  return (
    <div className={styles.container}>
      {isRefreshing ? (
        <div
          className={styles.refreshOverlay}
          role="status"
          aria-live="polite"
          aria-label="Updating timesheet"
        >
          <TailSpin
            height="48"
            width="48"
            color="var(--primary-color)"
            visible
          />
        </div>
      ) : null}
      <TimesheetEntryCard
        entries={entries}
        handleAddEntry={handleAddEntry}
        handleDeleteEntry={handleDeleteEntry}
        handleEditEntry={handleEditEntry}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        isSubmitted={isSubmitted}
        isExpired={statusInfo?.expired}
        showExtendButton={showExtendButton}
        extensionHrStatus={extensionHrStatus}
        onExtendClick={onExtendClick}
        isExtendLoading={isExtendLoading}
        projectAllocations={projectAllocations ?? statusInfo?.projectAllocations}
        getOtherEntriesMinutesForProject={getOtherEntriesMinutesForProject}
        historicalViewOnly={historicalViewOnly}
        timesheetLockMessage={timesheetLockMessage}
      />
      {!statusInfo?.expired && !historicalViewOnly && (
        <ActionBanner
          state={isSubmitted ? "submitted" : (draftId || isDraftSavedLocally) ? "draft" : "pending"}
          hasEntries={entries.length > 0}
          correctionMode={correctionMode}
          onSaveDraft={handleSaveDraft}
          onOpenSubmit={() => setIsSubmitConfirmOpen(true)}
          isSavingDraft={isSavingDraft}
          isSubmitting={isSubmitting}
          hasModifiedEntries={
            correctionMode ? entries.some((e) => e.isModified) : true
          }

        />
      )}

      <SubmitConfirmationModal
        open={isSubmitConfirmOpen}
        onClose={() => setIsSubmitConfirmOpen(false)}
        onConfirm={handleConfirmSubmit}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default TimeEntryTab;
